import { useSDK, useIdToken } from "@sans-service-tier/sdk-react";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { clearAuthCookies, normalizeLogout } from "../util/auth";

const BASENAME = "/workbook";
const RETURN_KEY = "sans:returnTo";

// must be under /workbook on the wire
const isSafeFullPath = (p?: string | null) => !!p && p.startsWith(BASENAME);

// convert full (/workbook/...) -> app-relative (/...)
const toAppPath = (full?: string | null) => {
  if (!full) return "/";
  return full.startsWith(BASENAME) ? full.slice(BASENAME.length) || "/" : full;
};

// ensure trailing "/" for top-level slug (helps mkdocs relative assets)
const ensureSlashForTopSlug = (p: string) => (/^\/[a-z]{3}\d{3}[a-z]?-[a-z]\d{2}$/i.test(p) ? p + "/" : p);

export function Authorizer({ adapter }): React.JSX.Element | null {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loading } = useIdToken();
  const sdk = useSDK();

  // Capture ?returnTo=/workbook/... and store as app-relative
  useEffect(() => {
    const rtFull = params.get("returnTo");
    if (isSafeFullPath(rtFull)) {
      const appPath = ensureSlashForTopSlug(toAppPath(rtFull!));
      sessionStorage.setItem(RETURN_KEY, appPath);
    }
  }, [params]);

  useEffect(() => {
    if (!sdk || loading) return;

    adapter.processLocation({}).then(({ queryArgs: { state, logout } }) => {
      const isLogout = normalizeLogout(logout);
      const idToken = sdk.api.tokens?.id;

      if (idToken) {
        // Prefer OIDC state (already app-relative), then stored app-relative, then "/"
        const stateApp = typeof state === "string" ? ensureSlashForTopSlug(state) : "";
        const storedApp = sessionStorage.getItem(RETURN_KEY) || "";
        sessionStorage.removeItem(RETURN_KEY);

        const dest = stateApp || storedApp || "/";
        // Always hard-redirect for *any* workbook path (not just top-level slug)
        if (dest.startsWith("/")) {
          // e.g. "/workbook" + "/tst999-j01/..." -> "/workbook/tst999-j01/..."
          // also future-proofs in the event `/workbook` => `/workbook/`
          const full = `${BASENAME.replace(/\/$/, "")}${dest.startsWith("/") ? "" : "/"}${dest}`;
          window.location.replace(full);
        } else {
          // fallback for anything weird (shouldn't happen)
          navigate("/", { replace: true });
        }
        return;
      }

      if (isLogout) {
        clearAuthCookies();
        sessionStorage.clear();
        window.open(BASENAME, "_self");
        return;
      }

      // Not logged in yet -> start login with app-relative state
      const storedApp = sessionStorage.getItem(RETURN_KEY) || "/";
      adapter.login({ state: ensureSlashForTopSlug(storedApp) });
    });
  }, [adapter, loading, navigate, sdk]);

  return <div>Redirecting...</div>;
}
